<template>
  <div class="rich-text-editor">
    <div class="editor-menu-bar" v-if="editor">
      <el-button-group>
        <el-button @click="editor.chain().focus().toggleBold().run()" 
                   :type="editor.isActive('bold') ? 'primary' : ''">
          <i class="fas fa-bold"></i>
        </el-button>
        <el-button @click="editor.chain().focus().toggleItalic().run()" 
                   :type="editor.isActive('italic') ? 'primary' : ''">
          <i class="fas fa-italic"></i>
        </el-button>
        <el-button @click="editor.chain().focus().toggleHeading({ level: 2 }).run()" 
                   :type="editor.isActive('heading', { level: 2 }) ? 'primary' : ''">H2</el-button>
        <el-button @click="editor.chain().focus().toggleHeading({ level: 3 }).run()" 
                   :type="editor.isActive('heading', { level: 3 }) ? 'primary' : ''">H3</el-button>
        <el-button @click="editor.chain().focus().toggleBulletList().run()" 
                   :type="editor.isActive('bulletList') ? 'primary' : ''">
          <i class="fas fa-list-ul"></i>
        </el-button>
        <el-button @click="editor.chain().focus().toggleOrderedList().run()" 
                   :type="editor.isActive('orderedList') ? 'primary' : ''">
          <i class="fas fa-list-ol"></i>
        </el-button>
        <el-button @click="editor.chain().focus().toggleBlockquote().run()" 
                   :type="editor.isActive('blockquote') ? 'primary' : ''">
          <i class="fas fa-quote-right"></i>
        </el-button>
        <el-button @click="setLink">
          <i class="fas fa-link"></i>
        </el-button>
        <el-button @click="addImage">
          <i class="fas fa-image"></i>
        </el-button>
      </el-button-group>
    </div>
    <editor-content :editor="editor" />
  </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import { Markdown } from 'tiptap-markdown'
import { ElMessageBox } from 'element-plus'

export default {
  components: {
    EditorContent,
  },
  props: {
    modelValue: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      editor: null,
    }
  },
  watch: {
    modelValue(value) {
      const editorContent = this.editor.storage.markdown.getMarkdown();
      if (value !== editorContent) {
        this.editor.commands.setContent(value || '', false)
      }
    }
  },
  mounted() {
    this.editor = new Editor({
      extensions: [
        StarterKit,
        Link.configure({
          openOnClick: false,
        }),
        Image,
        Markdown.configure({
          html: true,
          tightLists: true,
          breaks: true,
        }),
      ],
      content: this.modelValue || '',
      onUpdate: ({ editor }) => {
        const markdown = editor.storage.markdown.getMarkdown();
        this.$emit('update:modelValue', markdown);
      },
    })
  },
  beforeUnmount() {
    this.editor.destroy()
  },
  methods: {
    setLink() {
      const previousUrl = this.editor.getAttributes('link').href
      ElMessageBox.prompt('Enter the URL', 'Link', {
        inputValue: previousUrl,
        inputValidator: (value) => {
          if (!value) return 'URL is required'
          return true
        },
      }).then(({ value: url }) => {
        if (url === null) {
          return
        }
        if (url === '') {
          this.editor.chain().focus().extendMarkRange('link').unsetLink().run()
          return
        }
        this.editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
      }).catch(() => {
        // Cancelled
      })
    },
    addImage() {
      ElMessageBox.prompt('Enter the image URL', 'Insert Image', {
        inputValidator: (value) => {
          if (!value) return 'URL is required'
          return true
        },
      }).then(({ value: url }) => {
        if (url) {
          this.editor.chain().focus().setImage({ src: url }).run()
        }
      }).catch(() => {
        // Cancelled
      })
    },
  },
}
</script>

<style scoped>
.rich-text-editor {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  width: 100%;
}
.editor-menu-bar {
  padding: 5px;
  border-bottom: 1px solid #dcdfe6;
}
:deep(.ProseMirror) {
  min-height: 200px;
  padding: 10px;
}
</style>
